import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function PayPalCheckout({ onSuccess, userEmail }) {
  const paypalRef = useRef();
  const [amount, setAmount] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState("");
  const [resumeCreated, setResumeCreated] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);
  const [resumeMessageShown, setResumeMessageShown] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isPaymentPhase, setIsPaymentPhase] = useState(false);
  const [uploadedResumeUrl, setUploadedResumeUrl] = useState(null);

  // Fetch price
  useEffect(() => {
    fetch("https://resumefuel.xyz/resume/src/backend/get_paypal_amount.php")
      .then((res) => res.json())
      .then((data) => {
        const price = parseFloat(data?.amount);
        const finalPrice = !isNaN(price) ? price : 10;
        setAmount(finalPrice);
        setDiscountedAmount(finalPrice);
      })
      .catch(() => {
        setAmount(10);
        setDiscountedAmount(10);
      });
  }, []);

  // Render PayPal buttons
  useEffect(() => {
    if (!resumeCreated || !paypalRef.current || discountedAmount === null) return;

    paypalRef.current.innerHTML = "";

    const renderPayPal = () => {
      window.paypal.Buttons({
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [
              {
                description: "Resume Template",
                amount: {
                  currency_code: "USD",
                  value: discountedAmount.toFixed(2),
                },
              },
            ],
          });
        },
        onApprove: async (data, actions) => {
          const order = await actions.order.capture();
          const { id: orderID, payer, purchase_units } = order;
          const payerName = `${payer.name.given_name} ${payer.name.surname}`;
          const payerEmail = userEmail;
          const paidAmount = purchase_units[0].amount.value;

          setIsPaymentPhase(true);
          setUploading(true);

          try {
            if (!uploadedResumeUrl) throw new Error("Resume not uploaded yet!");

            const res = await fetch("https://resumefuel.xyz/resume/src/record_payment.php", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                orderID,
                payerName,
                payerEmail,
                amount: parseFloat(paidAmount),
                resume_url: uploadedResumeUrl,
              }),
            });

            const data = await res.json();
            if (data.success) {
              alert("✅ Payment and resume recorded successfully.");
              onSuccess?.({ orderID, payerName, payerEmail, amount: parseFloat(paidAmount) });
            } else {
              alert("⚠️ Resume uploaded, but DB failed: " + data.message);
            }
          } catch (err) {
            console.error("❌ Error during payment or upload", err);
            alert("❌ Payment succeeded, but resume saving failed.");
          } finally {
            setUploading(false);
          }
        },
        onError: (err) => {
          console.error("PayPal error:", err);
          alert("Payment failed. Please try again.");
        },
      }).render(paypalRef.current);
    };

    const PAYPAL_CLIENT_ID =
      "AQkymdW8fGb2agwPPH3-prNR_F5A2XxTxTgGnhNjyuGdliOLsoWlsBgr4PqJfIjLMchSh_rOURoA0Nko";

    if (window.paypal) renderPayPal();
    else if (!document.getElementById("paypal-sdk")) {
      const script = document.createElement("script");
      script.id = "paypal-sdk";
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
      script.onload = renderPayPal;
      document.body.appendChild(script);
    }
  }, [resumeCreated, discountedAmount, onSuccess, uploadedResumeUrl]);

  const applyCoupon = () => {
    if (!coupon.trim()) return;

    fetch("https://resumefuel.xyz/resume/src/validate_coupon.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: coupon }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.valid && amount) {
          const discount = amount * (data.discount / 100);
          setDiscountedAmount(amount - discount);
          setApplied(true);
          setError("");
        } else {
          setError("❌ Invalid coupon code");
          setApplied(false);
          setDiscountedAmount(amount);
        }
      })
      .catch(() => setError("❌ Error validating coupon"));
  };

  const uploadPdf = async (blob) => {
    const formData = new FormData();
    const filename = "resume_" + Date.now() + ".pdf"; // dynamic filename
    formData.append("file", blob, filename);

    try {
      const response = await fetch("https://resumefuel.xyz/resume/src/upload_resume.php", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (data.success) return data.url;
      alert("⚠️ Resume upload failed: " + data.message);
      return null;
    } catch (error) {
      alert("❌ Error uploading resume");
      console.error(error);
      return null;
    }
  };

  const handleCreateResume = async () => {
    if (uploading) return;

    const pages = Array.from(document.querySelectorAll("div[id^='resume-page']"));
    if (!pages.length) {
      alert("❌ Resume preview pages not found.");
      return;
    }

    // Wait for dynamic content to fully render
    await new Promise(resolve => setTimeout(resolve, 300)); // 300ms delay

    setIsPaymentPhase(false);
    setUploading(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
      }

      setGeneratedPdf(pdf);

      const uploadedUrl = await uploadPdf(pdf.output("blob"));
      if (!uploadedUrl) {
        alert("❌ Failed to upload resume. Please try again.");
        setUploading(false);
        return;
      }

      setUploadedResumeUrl(uploadedUrl);
      setResumeCreated(true);
      setResumeMessageShown(true);
    } catch (err) {
      console.error("❌ Error generating or uploading PDF", err);
      alert("❌ Failed to create resume PDF.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="payment-txt">
      <h3>Buy Resume Template</h3>

      {amount !== null ? (
        <>
          <p>
            Price: <strong>${discountedAmount.toFixed(2)}</strong>
            {applied && (
              <span style={{ textDecoration: "line-through", marginLeft: "10px", color: "gray" }}>
                ${amount.toFixed(2)}
              </span>
            )}
          </p>

          {!applied && (
            <div style={{ marginBottom: "20px" }}>
              <input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Enter coupon code"
                style={{ padding: "10px", fontSize: "16px" }}
              />
              <button onClick={applyCoupon} style={{ marginLeft: "10px", padding: "10px 20px" }}>
                Apply
              </button>
            </div>
          )}

          {error && <p style={{ color: "red" }}>{error}</p>}
          {applied && <p style={{ color: "green" }}>✅ Coupon applied!</p>}

          {!resumeCreated && (
            <button
              onClick={handleCreateResume}
              style={{
                padding: "10px 20px",
                marginBottom: "20px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              Create Resume
            </button>
          )}

          {resumeMessageShown && (
            <p style={{ color: "green", marginBottom: "10px" }}>
              ✅ Resume created successfully! Please proceed for the payment.
            </p>
          )}

          {uploading ? (
            <div style={{ marginTop: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="spinner" />
              <span style={{ color: "#007bff", fontWeight: "bold" }}>
                {isPaymentPhase ? "💳 Finalizing payment..." : "📝 Creating your resume..."}
              </span>
            </div>
          ) : (
            resumeCreated && <div ref={paypalRef} style={{ minHeight: "40px" }}></div>
          )}
        </>
      ) : (
        <p>Loading price...</p>
      )}
    </div>
  );
}

export default PayPalCheckout;